import type { V2 } from '../core/math';
import type { DamageSide } from '../core/events';
import type { Body } from '../physics/body';

/**
 * Vehicle contract. Owner: game-director (src/vehicles).
 *
 * Who drives: whoever owns `vehicle.controller` writes `vehicle.controls` every fixed step BEFORE
 * the vehicle system runs (order band 200–299). The vehicle system (order 300) turns controls into
 * forces; physics (400) integrates. AI never moves a vehicle's body directly.
 */

/** Per-step driving input. */
export interface VehicleControls {
  /** -1..1: + accelerate, - brake (and reverse once stopped). */
  throttle: number;
  /** -1..1: + steer right, - steer left. */
  steer: number;
  handbrake: boolean;
}

export type VehicleController = 'player' | 'ai' | 'none';

/** Spec ids. world/traffic/police may add more specs to VEHICLE_SPECS. */
export type VehicleSpecId = 'compact' | 'saloon' | 'van' | 'coupe' | 'police' | 'cab' | 'lorry' | 'estate' | 'exec' | 'roadster' | (string & {});

export type VehicleClass = 'compact' | 'saloon' | 'van' | 'sports' | 'police' | 'service' | 'heavy';

/** Silhouette family used by the procedural mesh generator. */
export type VehicleBodyStyle = 'hatch' | 'saloon' | 'van' | 'coupe' | 'cab' | 'lorry' | 'estate' | 'exec' | 'roadster';

/**
 * Data-driven handling + presentation spec. See docs/DRIVING.md for what each number does and why
 * the roster is tuned the way it is.
 */
export interface VehicleSpec {
  id: VehicleSpecId;
  /** Original fictional make/model shown in UI. */
  displayName: string;
  class: VehicleClass;
  body: VehicleBodyStyle;
  /** kg — also drives how hard it rams (impulse) and how little it suffers from hits. */
  mass: number;
  /** Collision box (m). */
  width: number;
  length: number;
  height: number;
  /** Distance between axles (m) — drives low-speed turning radius. */
  wheelbase: number;
  /** Fraction of weight on the front axle (0.4–0.65). Positions the CG between the axles. */
  frontWeight: number;
  drive: 'front' | 'rear';
  /** Peak forward acceleration at low speed (m/s²). */
  accel: number;
  /** Top speed forward (m/s). */
  maxSpeed: number;
  /** Top speed in reverse (m/s). */
  maxReverse: number;
  /** Braking deceleration (m/s²). */
  brake: number;
  /** Off-throttle deceleration at top speed (m/s²). */
  engineBrake: number;
  /** Tyre friction coefficient per axle (lateral g the axle can hold). front < rear = stable/understeer. */
  frontGrip: number;
  rearGrip: number;
  /** 0..1 how much lateral slip each axle cancels per step while gripping (lower = softer, slidier). */
  tyreStiffness: number;
  /** Rear-grip multiplier while the handbrake is held (0.2 = very loose). */
  handbrakeGrip: number;
  /** How much throttle eats the driven axle's lateral grip (0 = none, 1 = strong power oversteer). */
  powerOversteer: number;
  /** Max front wheel angle at standstill (rad). */
  steerAngle: number;
  /** Speed (m/s) at which steering angle has fallen to half. */
  steerFalloffSpeed: number;
  /** Steering angle never drops below this fraction at speed. */
  minSteer: number;
  /** How quickly the steering input itself moves (1/s). */
  steerSpeed: number;
  /** Counter-steer assist gain (front wheels steer into slides automatically). */
  counterSteer: number;
  /** Yaw damping (1/s) applied when the driver isn't steering — helps the car settle. */
  yawDamping: number;
  /** Longitudinal load transfer strength (braking loads the front → sharper turn-in). */
  weightTransfer: number;
  /** Aerodynamic drag coefficient (acc = drag * v²). */
  drag: number;
  /** Rolling resistance deceleration (m/s²). */
  rolling: number;
  /** Multiplier on box moment of inertia (heavier feel, less spin on impacts). */
  inertiaScale: number;
  maxHealth: number;
  /** Damage taken multiplier (compact hatch is fragile, lorry barely notices). */
  fragility: number;
  /** Collision restitution (bounciness). */
  restitution: number;
  /** Simulated gear count for engine audio. */
  gears: number;
  /** Engine idle and redline rpm for audio. */
  idleRpm: number;
  redlineRpm: number;
  /** Default paint colours (hex) to choose from. */
  paints: number[];
  /** Traffic spawn weight (0 = never as civilian traffic). */
  trafficWeight: number;
}

export interface VehicleDamage {
  front: number;
  rear: number;
  left: number;
  right: number;
}

/**
 * Read-only telemetry produced by the vehicle model each step — the contract for audio, fx,
 * camera and UI. Wheel arrays are ordered [frontLeft, frontRight, rearLeft, rearRight].
 */
export interface VehicleTelemetry {
  /** Signed forward speed (m/s); negative when reversing. */
  forwardSpeed: number;
  /** Sideways slip speed (m/s, + = sliding right). */
  lateralSpeed: number;
  /** 0..1 how much the tyres are sliding (max over wheels): tyre squeal volume. */
  skid: number;
  /** Per-wheel skid intensity 0..1 (skid marks, tyre smoke). */
  wheelSkid: [number, number, number, number];
  /** Axle slip angles (rad). */
  frontSlip: number;
  rearSlip: number;
  /** 0..1 wheelspin under hard acceleration. */
  wheelspin: number;
  /** True while braking (brake lights). */
  braking: boolean;
  reversing: boolean;
  /** Smoothed steering wheel position -1..1 (includes counter-steer assist). */
  steer: number;
  /** Front wheel angle (rad, + = right). */
  wheelAngle: number;
  /** Smoothed longitudinal acceleration (m/s²) — body pitch, load transfer. */
  longAccel: number;
  /** Lateral acceleration (m/s², + = right) — body roll. */
  latAccel: number;
  gear: number;
  /** Simulated engine rpm. */
  rpm: number;
  /** 0..1 engine load (throttle * power) for audio. */
  load: number;
  /** Throttle actually applied (-1..1) for audio. */
  throttle: number;
  /** 0..1 metal-on-something scrape intensity this step (sparks, scrape sound). */
  scrape: number;
  /** Closing speed (m/s) of the hardest impact this step, 0 if none (impact sound). */
  impact: number;
  /** Effective top speed after damage (m/s). */
  topSpeed: number;
}

/** Fire/smoke state derived from health. */
export type VehicleCondition = 'ok' | 'smoking' | 'heavySmoke' | 'burning' | 'wrecked';

export type Occupant = { kind: 'player' } | { kind: 'ped'; pedId: number | null };

/** Live vehicle entity. Obtain via the `vehicles` service; never construct directly. */
export interface Vehicle {
  readonly id: number;
  readonly spec: VehicleSpec;
  readonly body: Body;
  controls: VehicleControls;
  controller: VehicleController;
  /** Driver (single seat model for the slice). */
  occupant: Occupant | null;
  readonly isPolice: boolean;
  /** Player-owned (bought / mission reward / already stolen). Taking it is not a crime. */
  ownedByPlayer: boolean;
  /** Has been stolen by the player at some point (police may recognise it). */
  stolen: boolean;
  locked: boolean;
  health: number;
  damage: VehicleDamage;
  /** Tyres shot out or spiked, per side (docs/POLICE-AI-POLISH.md): each takes grip and top speed off and the car pulls to that side. */
  punctures: { left: number; right: number };
  /** True once health hit zero (burning or wrecked). */
  destroyed: boolean;
  condition: VehicleCondition;
  /** Seconds left before a burning vehicle explodes. */
  fireTimer: number;
  /** Sim time this vehicle last took damage the player caused (a shot, a blast, a contact with their car). */
  readonly lastPlayerHitTime: number;
  /** Increments whenever damage changes (views re-deform the mesh lazily). */
  readonly damageVersion: number;
  /** Local-space impact points with dent depth (m), newest last. */
  readonly dents: readonly { x: number; z: number; depth: number }[];
  /** Siren / lightbar active (police). */
  siren: boolean;
  headlights: boolean;
  paint: number;
  telemetry: VehicleTelemetry;
  /** Free-form tags other modules may attach (e.g. 'mission:target', 'traffic'). */
  tags: Set<string>;
  /** Module-specific per-vehicle data, keyed by module name. */
  data: Record<string, unknown>;

  readonly pos: V2;
  readonly heading: number;
  readonly velocity: V2;
  /** |velocity| m/s */
  readonly speed: number;
}

export interface SpawnOptions {
  heading?: number;
  controller?: VehicleController;
  paint?: number;
  ownedByPlayer?: boolean;
  locked?: boolean;
  /** Start asleep (parked cars). */
  parked?: boolean;
  tags?: string[];
  occupant?: Occupant | null;
}

export const DAMAGE_SIDES: readonly DamageSide[] = ['front', 'rear', 'left', 'right'];

/**
 * Tag on a vehicle whose state belongs to the online server (another player's car, or a networked car
 * nobody local is driving). The net module moves it kinematically from snapshots; local physics still
 * collides with it, but local damage/fire never applies (health comes from the server).
 */
export const NET_REMOTE_TAG = 'net:remote';
/** Tag on every local vehicle bound to a networked (server) vehicle; the net module manages its lifetime. */
export const NET_CAR_TAG = 'net:car';
/** The player's kept car offline: the Lockup 7 car (economy) and the garage's "Keep this one" motor (activities). */
export const LOCKUP_CAR_TAG = 'lockup:car';
export const MOTOR_TAG = 'garage:motor';
